import { EventEmitter } from "@stencil/core";
import { LocalizableComponent, DefaultLocalization, setCurrentLocalizationDelegate } from "./localizable-component";

export interface FormComponent<T, U extends DefaultLocalization> extends LocalizableComponent<U> {
  name: string;
  label: string;
  value: string;
  disabled: boolean;
  required: boolean;
  invalid: boolean;
  valueChanged: EventEmitter<T>;
  errorMessage: string;
  customErrorMessage: string;
  customValidatorHasError: boolean;
  customRequiredMessage: string;
  validateRequired(): Promise<boolean>;
  handleCustomValidation(errorMessage: string): Promise<void>;
  validate(): Promise<boolean>;
}

/**
 * Saves the given error message and assumes that the component is invalid if any error message is provided.
 * Define the calling wrapper function as @Method() in your component to be able to call it from outside.
 * @param component The FormComponent that should handle the custom validation.
 * @param errorMessage An error message of a custom validator. If none is supplied, the custom validation is considered to be valid.
 */
export async function handleCustomValidationDelegate<T, U extends DefaultLocalization>(
  component: FormComponent<T, U>,
  errorMessage: string
): Promise<void> {
  component.customErrorMessage = "";
  const hasError = errorMessage != null && errorMessage.length > 0;
  component.customErrorMessage = hasError ? errorMessage : component.errorMessage;
  component.customValidatorHasError = hasError;
  await component.validate();
}

/**
 * Validates that the component has a value if the required attribute is set.
 */
export async function validateRequiredValueDelegate<T, U extends DefaultLocalization>(
  component: FormComponent<T, U>
): Promise<boolean> {
  const hasValue = component.value != null && component.value.length > 0;
  const isInvalid = component.required && !hasValue;
  if (!component.currentLocalization) {
    setCurrentLocalizationDelegate(component);
  }
  const requiredMessage = component.customRequiredMessage
    ? component.customRequiredMessage
    : component.currentLocalization.required;
  if (isInvalid) component.errorMessage = requiredMessage;
  return !isInvalid;
}

/**
 * First validates that the component has a value if it is required, then runs custom validation.
 */
export async function validateDelegate<T, U extends DefaultLocalization>(
  component: FormComponent<T, U>
): Promise<boolean> {
  const isRequiredValid = await component.validateRequired();
  component.invalid = !isRequiredValid || component.customValidatorHasError;
  component.errorMessage = !isRequiredValid ? component.errorMessage : component.customErrorMessage;
  return !component.invalid;
}
